// Hybrid Tagger - On-device first, cloud fallback
// Uses Chrome Writer API for tag extraction

import { ChromeAIDetector } from './chrome-ai-detector';
import { API_BASE_URL } from '../config/settings';

export interface TagResult {
  tags: string[];
  method: 'on-device' | 'cloud';
  duration: number;
  cost: number;
}

export class HybridTagger {
  private static MAX_ON_DEVICE_LENGTH = 5000; // chars

  /**
   * Extract tags from content
   */
  static async extractTags(content: string, title?: string): Promise<TagResult> {
    const startTime = Date.now();

    // Try on-device first
    if (await this.canUseOnDevice(content.length)) {
      try {
        const tags = await this.extractOnDevice(content, title);

        this.trackUsage('on-device', Date.now() - startTime, 0);

        return {
          tags,
          method: 'on-device',
          duration: Date.now() - startTime,
          cost: 0
        };
      } catch (error) {
        console.warn('⚠️ On-device tag extraction failed, falling back to cloud:', error);
      }
    }

    // Fallback to cloud
    const tags = await this.extractCloud(content, title);

    this.trackUsage('cloud', Date.now() - startTime, 0.0001);

    return {
      tags,
      method: 'cloud',
      duration: Date.now() - startTime,
      cost: 0.0001
    };
  }

  /**
   * Check if we can use on-device
   */
  private static async canUseOnDevice(contentLength: number): Promise<boolean> {
    if (contentLength > this.MAX_ON_DEVICE_LENGTH) {
      return false;
    }

    const availability = await ChromeAIDetector.getCached();
    return availability.writer?.canUse === true;
  }

  /**
   * Extract tags using Chrome Writer API (on-device)
   */
  private static async extractOnDevice(content: string, title?: string): Promise<string[]> {
    // @ts-ignore - Chrome AI API
    const writer = await self.ai.writer.create({
      sharedContext: 'Extract relevant tags or keywords from the text. Return as comma-separated list.'
    });

    // Build prompt
    let prompt = '';
    if (title) {
      prompt += `Title: ${title}\n\n`;
    }

    // Truncate content if needed
    const truncated = content.length > this.MAX_ON_DEVICE_LENGTH
      ? content.slice(0, this.MAX_ON_DEVICE_LENGTH)
      : content;

    prompt += `Extract 5-10 relevant tags from this content:\n\n${truncated}\n\nTags (comma-separated):`;

    const response = await writer.write(prompt);

    // Cleanup
    writer.destroy();

    // Parse response
    return this.parseTags(response);
  }

  /**
   * Extract tags using cloud AI
   */
  private static async extractCloud(content: string, title?: string): Promise<string[]> {
    const response = await fetch(`${API_BASE_URL}/ai/extract-tags`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        content,
        title
      })
    });

    if (!response.ok) {
      throw new Error('Cloud tag extraction failed');
    }

    const data = await response.json();
    return data.tags || data.data?.tags || [];
  }

  /**
   * Parse tags from AI response
   */
  private static parseTags(response: string): string[] {
    // Try to extract tags from response
    const tagPattern = /(?:tags?:?|keywords?:?)\s*(.+)/i;
    const match = response.match(tagPattern);

    const tagsString = match ? match[1] : response;

    // Split by comma, clean up
    const tags = tagsString
      .split(/[,;]/)
      .map(tag => tag.trim().toLowerCase())
      .filter(tag => tag.length > 0 && tag.length < 50) // Reasonable length
      .filter(tag => !/^(and|or|the|a|an|in|on|at|to|for|of|with)$/.test(tag)) // Remove stop words
      .slice(0, 10); // Max 10 tags

    // Remove duplicates
    return [...new Set(tags)];
  }

  /**
   * Track usage statistics
   */
  private static trackUsage(method: 'on-device' | 'cloud', duration: number, cost: number): void {
    const stats = this.getStats();

    if (method === 'on-device') {
      stats.onDevice.count++;
      stats.onDevice.totalDuration += duration;
    } else {
      stats.cloud.count++;
      stats.cloud.totalDuration += duration;
      stats.cloud.totalCost += cost;
    }

    localStorage.setItem('ai_tagger_stats', JSON.stringify(stats));
  }

  /**
   * Get usage statistics
   */
  static getStats() {
    const cached = localStorage.getItem('ai_tagger_stats');
    return cached ? JSON.parse(cached) : {
      onDevice: { count: 0, totalDuration: 0 },
      cloud: { count: 0, totalDuration: 0, totalCost: 0 }
    };
  }

  /**
   * Clear statistics
   */
  static clearStats(): void {
    localStorage.removeItem('ai_tagger_stats');
  }
}
